// Performance V2 — Overview tab (#4051). The customisable widget grid:
// account snapshot (#4259), problem alerts, LLM insight cards (#4304), next
// steps (#4305), metric delta cards, money context, type efficiency,
// conversion-mix donut (#4301), notable search terms, the shared performance
// chart, AI-actions timeline and top performers.
// Extracted from performance-v2.tsx (#4932 Phase 0.3); no behavior change.
// All widget data is computed in shell memos (most of it is shared with the
// dedicated tabs) and arrives as props; the shared chart arrives as a
// renderChart render prop.
import { Spinner } from '@/components/glitch-ui/spinner';
import type React from 'react';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, TrendingDown, AlertCircle, CheckCircle,
  Sparkles, Trophy,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';

import type { AccountSearchTerm, ConversionActionRow, PerformanceInsight, InsightSentiment, RecommendationItem } from '@/lib/apis';
import type { ActionLog } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { cn } from '@/lib/utils';
import { explainConversionMix, explainSearchTerms } from '../explainers';
import { fmt, MetricCard, DeltaLine, EmptyChart, CHART_PURPLE, parseCampaignName } from '../cells';
import { formatActivityTimestamp } from './activity-tab';
import type { TypeMetrics, CampaignMetrics } from './campaigns-tab';
import type { AdMetric } from './ads-tab';
import type { BudgetRow } from './budget-tab';

const ACTION_TYPE_LABELS: Record<string, string> = {
  DELETE_KEYWORD: 'Remove Keyword',
  ADD_KEYWORD: 'Add Keyword',
  PAUSE_KEYWORD: 'Pause Keyword',
  ADD_NEGATIVE_KEYWORD: 'Add Negative',
  ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN: 'Add Negative',
  PAUSE_NEGATIVE_KEYWORD: 'Pause Negative',
  ADD_EXACT_MATCH_KEYWORD: 'Add Exact Match',
  SET_BUDGET: 'Set Budget',
  SET_TCPA: 'Set tCPA',
  CREATE_CAMPAIGN: 'Create Campaign',
  CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION: 'Create Campaign',
  CREATE_HIPO_CAMPAIGN: 'Create High Performance Campaign',
  CREATE_LOCATION_CAMPAIGN: 'Create Location Campaign',
  CREATE_ICP_CAMPAIGN: 'Create ICP Campaign',
  SYNC_CAMPAIGN_DATA: 'Campaign Sync',
  GOOGLE_ADS_SYNC: 'Google Sync',
};

const ACTION_TYPE_COLORS: Record<string, string> = {
  DELETE_KEYWORD: 'bg-red-100 text-red-800',
  ADD_KEYWORD: 'bg-green-100 text-green-800',
  PAUSE_KEYWORD: 'bg-yellow-100 text-yellow-800',
  ADD_NEGATIVE_KEYWORD: 'bg-blue-100 text-blue-800',
  ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN: 'bg-blue-100 text-blue-800',
  PAUSE_NEGATIVE_KEYWORD: 'bg-blue-100 text-blue-800',
  ADD_EXACT_MATCH_KEYWORD: 'bg-indigo-100 text-indigo-800',
  SET_BUDGET: 'bg-purple-100 text-purple-800',
  SET_TCPA: 'bg-pink-100 text-pink-800',
  CREATE_CAMPAIGN: 'bg-teal-100 text-teal-800',
  CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION: 'bg-teal-100 text-teal-800',
  CREATE_HIPO_CAMPAIGN: 'bg-teal-100 text-teal-800',
  CREATE_LOCATION_CAMPAIGN: 'bg-teal-100 text-teal-800',
  CREATE_ICP_CAMPAIGN: 'bg-teal-100 text-teal-800',
  SYNC_CAMPAIGN_DATA: 'bg-gray-100 text-gray-900',
  GOOGLE_ADS_SYNC: 'bg-gray-100 text-gray-900',
};

export function OverviewTab({
  hasData,
  accountSnapshot,
  problemAlerts,
  spendTrends,
  insightsData,
  isInsightsLoading,
  isSearchTermsLoading,
  isConversionActionsLoading,
  notableSearchTermsByCampaign,
  conversionMixByAction,
  totals,
  previousTotals,
  topRecommendationsData,
  isTopRecommendationsLoading,
  typeMetrics,
  costByType,
  convByType,
  conversionActionsData,
  searchTermsData,
  adMetrics,
  actionLogs,
  budgetUtilisation,
  filteredCampaigns,
  currencySymbol,
  onNavigateTab,
  renderChart,
  layout,
  onReorder,
  onResize,
}: {
  hasData: boolean;
  accountSnapshot: { optScore: number | null; isCoverage: number | null; budgetUtil: number | null; changeVelocity: number | null };
  problemAlerts: { severity: 'warning' | 'critical'; message: string; campaign?: string }[];
  spendTrends: {
    cpa: { current: number; delta: number; hasData: boolean };
    roas: { current: number; delta: number; hasData: boolean };
  } | null;
  insightsData: { insights?: PerformanceInsight[] } | undefined;
  isInsightsLoading: boolean;
  isSearchTermsLoading: boolean;
  isConversionActionsLoading: boolean;
  notableSearchTermsByCampaign: { campaign: string; terms: AccountSearchTerm[]; totalClicks: number }[];
  conversionMixByAction: { name: string; conversions: number; value: number }[];
  totals: {
    impressions: number; clicks: number; conversions: number; cost: number;
    conversionValue: number; allConversions: number; viewThrough: number;
    ctr: number; cpc: number; cpa: number; roas: number; conversionRate: number;
  };
  /** Compare-period totals over the same filtered set (#6276); null when compare is off. */
  previousTotals: {
    impressions: number; clicks: number; conversions: number; cost: number;
    conversionValue: number; ctr: number; cpc: number; cpa: number; roas: number; conversionRate: number;
  } | null;
  topRecommendationsData: { items?: RecommendationItem[] } | undefined;
  isTopRecommendationsLoading: boolean;
  typeMetrics: TypeMetrics[];
  costByType: { name: string; value: number }[];
  convByType: { name: string; value: number }[];
  conversionActionsData: ConversionActionRow[] | undefined;
  searchTermsData: AccountSearchTerm[] | undefined;
  adMetrics: AdMetric[];
  actionLogs: ActionLog[];
  budgetUtilisation: BudgetRow[];
  filteredCampaigns: CampaignMetrics[];
  currencySymbol: string;
  onNavigateTab: (tab: 'overview' | 'campaigns' | 'ads' | 'keywords' | 'search-terms' | 'audience' | 'budget' | 'activity') => void;
  renderChart: () => React.ReactNode;
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const cs = currencySymbol;

  return (
        <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
          {(widgetId, widgetWidth) => {
            switch (widgetId) {
              case 'account-snapshot': {
                // #4259 — Account Snapshot: the four numbers the team
                // currently has to assemble by hand from separate Google
                // Ads exports. Each tile is clickable; clicks drill into
                // the relevant tab so users can go from "we're losing 30%
                // of impressions to budget" to the Budget tab in one move.
                const { optScore, isCoverage, budgetUtil, changeVelocity } = accountSnapshot;
                // Threshold-based colour for the four metrics. opt + IS:
                // higher is better; budget util: 80-99% is healthy, ≥100%
                // is saturated (amber alert), <50% is underspent (info).
                const optBadge = optScore == null
                  ? 'bg-gray-100 text-gray-500'
                  : optScore >= 0.75 ? 'bg-green-100 text-green-800'
                  : optScore >= 0.5 ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-700';
                const isBadge = isCoverage == null
                  ? 'bg-gray-100 text-gray-500'
                  : isCoverage >= 0.7 ? 'bg-green-100 text-green-800'
                  : isCoverage >= 0.3 ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-700';
                const budgetBadge = budgetUtil == null
                  ? 'bg-gray-100 text-gray-500'
                  : budgetUtil >= 100 ? 'bg-red-100 text-red-700'
                  : budgetUtil >= 80 ? 'bg-green-100 text-green-800'
                  : budgetUtil >= 50 ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-600';
                const velocityBadge = changeVelocity == null
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-[#f0eeff] text-[#290BD9]';

                type Tile = { label: string; value: string; subtitle: string; badgeCls: string; onClick: () => void };
                const tiles: Tile[] = [
                  {
                    label: 'Optimisation score',
                    value: optScore == null ? '—' : `${Math.round(optScore * 100)}`,
                    subtitle: optScore == null ? 'No campaigns with a score yet' : 'Out of 100 · weighted by impressions',
                    badgeCls: optBadge,
                    onClick: () => onNavigateTab('campaigns'),
                  },
                  {
                    label: 'Search impression share',
                    value: isCoverage == null ? '—' : `${(isCoverage * 100).toFixed(1)}%`,
                    subtitle: isCoverage == null ? 'No Search campaigns reporting impression share yet' : 'Impressions-weighted across Search campaigns',
                    badgeCls: isBadge,
                    onClick: () => onNavigateTab('campaigns'),
                  },
                  {
                    label: 'Budget utilisation',
                    value: budgetUtil == null ? '—' : `${budgetUtil.toFixed(0)}%`,
                    subtitle: budgetUtil == null ? 'No campaigns with daily budgets' : `Average across ${budgetUtilisation.length} campaign${budgetUtilisation.length === 1 ? '' : 's'}`,
                    badgeCls: budgetBadge,
                    onClick: () => onNavigateTab('budget'),
                  },
                  {
                    label: 'Change velocity',
                    value: changeVelocity == null ? '—' : `${changeVelocity.toFixed(1)}/wk`,
                    subtitle: changeVelocity == null ? 'No edits or Glitch actions recorded' : 'User edits + Glitch actions (30-day cap)',
                    badgeCls: velocityBadge,
                    onClick: () => onNavigateTab('activity'),
                  },
                ];
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    {/* pr-7: reserve the top-right corner for the WidgetGrid
                        resize/reorder kebab so it doesn't sit on top of the
                        subtitle on hover. */}
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Account snapshot</h4>
                      <span className="text-[10px] tracking-wider text-gray-400 uppercase">At-a-glance</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {tiles.map(t => (
                        <button
                          key={t.label}
                          type="button"
                          onClick={t.onClick}
                          className="text-left rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all p-3 bg-white"
                        >
                          <p className="text-[10px] tracking-wider text-gray-500 uppercase mb-1">{t.label}</p>
                          <div className="flex items-baseline gap-2">
                            <span className={cn('inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-base font-semibold', t.badgeCls)}>
                              {t.value}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">{t.subtitle}</p>
                        </button>
                      ))}
                    </div>
                  </Card>
                );
              }

              case 'problem-alerts':
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Alerts</h4>
                    {problemAlerts.length > 0 ? (
                      <div className="space-y-2">
                        {problemAlerts.map((alert, i) => (
                          <div
                            key={i}
                            className={`flex items-start gap-3 px-4 py-3 rounded-lg border-l-4 ${
                              alert.severity === 'critical'
                                ? 'bg-red-50 border-l-red-400 text-red-700'
                                : 'bg-amber-50 border-l-amber-400 text-amber-700'
                            }`}
                          >
                            <AlertCircle className={`h-4 w-4 mt-0.5 shrink-0 ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{alert.message}</p>
                              {alert.campaign && (
                                <p className={`text-xs mt-0.5 ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`}>{alert.campaign}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                        <span className="text-sm text-gray-500">No issues detected</span>
                      </div>
                    )}
                  </Card>
                );

              case 'insights': {
                // #4304 — LLM-generated narrative cards. Three-part semantic
                // colour follows the design system's border-l-4 pattern
                // (see frontend/CLAUDE.md). Cards fail soft — if the LLM
                // returns nothing we render a quiet empty state rather than
                // a loud error, so Overview stays usable.
                const sentimentStyles: Record<InsightSentiment, { border: string; bg: string; label: string }> = {
                  info:        { border: 'border-l-[#290BD9]', bg: 'bg-[#f0eeff]',   label: 'text-[#290BD9]' },
                  success:     { border: 'border-l-[#1a8a56]', bg: 'bg-[#f0fff6]',   label: 'text-[#1a8a56]' },
                  warning:     { border: 'border-l-[#e0a800]', bg: 'bg-[#fffbf0]',   label: 'text-[#856404]' },
                  opportunity: { border: 'border-l-[#c0398a]', bg: 'bg-[#fff0f8]',   label: 'text-[#8b1a5c]' },
                };
                const insights: PerformanceInsight[] = insightsData?.insights ?? [];
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Insights</h4>
                      <span className="text-[10px] tracking-wider text-gray-400 uppercase">Auto-generated</span>
                    </div>
                    {isInsightsLoading && insights.length === 0 ? (
                      <div className="flex items-center py-6 text-sm text-gray-500">
                        <Spinner size="sm" className="mr-2" />
                        Composing insights from the latest data…
                      </div>
                    ) : insights.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">
                        Insights will appear once campaigns have enough data in the selected period.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {insights.map((ins, i) => {
                          const style = sentimentStyles[ins.sentiment] ?? sentimentStyles.info;
                          return (
                            <div key={`${ins.category}-${i}`} className={cn('border-l-4 rounded-r-lg p-3', style.border, style.bg)}>
                              <p className={cn('text-[10px] font-bold tracking-wider uppercase mb-1', style.label)}>
                                {ins.category}
                              </p>
                              <p className="text-sm text-gray-700 leading-relaxed">{ins.narrative}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              }

              case 'next-steps': {
                // #4305 — Numbered next-step recommendations from the existing
                // optimisations engine. Each card has the same cobalt 1..N
                // circle, bold title, and ~2-sentence rationale as the team's
                // weekly client report. Fails soft when there are no pending
                // recommendations (Overview stays useful without).
                const recs: RecommendationItem[] = topRecommendationsData?.items ?? [];
                // Map recommendation_type → short kicker label (matches the
                // category badges already used on the Activity tab).
                const RECO_KICKER: Record<string, string> = {
                  NEGATIVE_KEYWORDS: 'Negative Keywords',
                  UNDERPERFORMING_KEYWORDS: 'Underperforming Keywords',
                  ZERO_IMPRESSION_KEYWORDS: 'Zero-Impression Keywords',
                  EXACT_MATCH_KEYWORDS: 'Exact-Match Keywords',
                  OPTIMISED_KEYWORDS: 'Optimised Keywords',
                  NEW_KEYWORDS: 'New Keywords',
                  HIGH_CPC: 'High CPC',
                  CHANGE_BUDGET: 'Budget',
                  BRAND_BUDGET: 'Brand Budget',
                  SET_TCPA: 'Target CPA',
                  NO_CONVERSION: 'No Conversions',
                  ALTERNATE_LOCATION: 'Alternate Location',
                };
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Next steps</h4>
                      <span className="text-[10px] tracking-wider text-gray-400 uppercase">From the optimisations engine</span>
                    </div>
                    {isTopRecommendationsLoading && recs.length === 0 ? (
                      <div className="flex items-center py-6 text-sm text-gray-500">
                        <Spinner size="sm" className="mr-2" />
                        Pulling the latest recommendations…
                      </div>
                    ) : recs.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">
                        No pending recommendations. New optimisations show up here as the engine detects them.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {recs.map((rec, i) => {
                          const kicker = RECO_KICKER[rec.recommendation_type] ?? rec.recommendation_type.replace(/_/g, ' ').toLowerCase();
                          return (
                            <div key={rec.id} className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="w-6 h-6 rounded-full bg-[#290BD9] text-white flex items-center justify-center text-xs font-bold">
                                  {i + 1}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  <span className="text-[10px] font-bold tracking-wider text-[#290BD9] uppercase">{kicker}</span>
                                  <span className="text-xs text-gray-400">·</span>
                                  <span className="text-xs text-gray-500 truncate max-w-[260px]">{rec.campaign_name}</span>
                                </div>
                                <p className="text-sm font-semibold text-gray-900 mb-0.5">{rec.title}</p>
                                <p className="text-xs text-gray-600 leading-relaxed">{rec.description || rec.content.slice(0, 200)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              }

              case 'notable-search-terms': {
                // #4307 — Chip-cloud preview of the Search Terms tab. Status-
                // coloured pills grouped by campaign. Clicking a chip jumps
                // into the Search Terms tab so users can drill from the
                // overview into the full table.
                const groups = notableSearchTermsByCampaign;
                const chipForStatus = (status: string | null): string => {
                  if (status === 'ADDED') return 'bg-green-50 text-green-800 border-green-200';
                  if (status === 'EXCLUDED') return 'bg-gray-100 text-gray-500 border-gray-200 line-through';
                  return 'bg-[#f0eeff] text-[#290BD9] border-[#dcd8f8]';
                };
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm relative">
                    <button
                      type="button"
                      onClick={() => onNavigateTab('search-terms')}
                      className="absolute top-2 right-10 text-xs text-[#290BD9] hover:text-[#2309C5] font-medium z-10"
                    >
                      View all →
                    </button>
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Notable search terms</h4>
                    </div>
                    {isSearchTermsLoading && groups.length === 0 ? (
                      <div className="flex items-center py-6 text-sm text-gray-500">
                        <Spinner size="sm" className="mr-2" />
                        Loading search terms…
                      </div>
                    ) : groups.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">
                        Search terms appear here once campaigns have impressions in the selected period.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                        {groups.slice(0, 6).map(group => (
                          <div key={group.campaign}>
                            <p className="text-[10px] font-bold tracking-wider text-[#290BD9] uppercase mb-1.5 truncate">
                              {group.campaign}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {group.terms.map(t => (
                                <button
                                  key={`${t.search_term}-${t.ad_group_id}`}
                                  type="button"
                                  onClick={() => onNavigateTab('search-terms')}
                                  className={cn(
                                    'inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium hover:shadow-sm transition-shadow whitespace-nowrap',
                                    chipForStatus(t.status),
                                  )}
                                  title={`${t.clicks} clicks · ${t.impressions} impr.`}
                                >
                                  {t.search_term}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        {groups.length > 6 && (
                          <p className="text-[10px] text-gray-400 pt-1">
                            +{groups.length - 6} more campaign{groups.length - 6 === 1 ? '' : 's'} on the Search Terms tab
                          </p>
                        )}
                      </div>
                    )}
                    {/* #4306 — auto-explainer footnote. */}
                    {(() => {
                      const narrative = explainSearchTerms(searchTermsData ?? []);
                      return narrative ? <p className="text-xs text-gray-400 mt-2 px-1">{narrative}</p> : null;
                    })()}
                  </Card>
                );
              }

              case 'overview-metrics': {
                // No spend/clicks/conversions in the window: show one clear
                // empty state rather than a grid of "-" cards, which reads as
                // broken (#4372).
                if (!hasData) {
                  return (
                    <Card className="border border-gray-200 shadow-sm">
                      <div className="flex flex-col items-center justify-center gap-1.5 py-10 text-center">
                        <BarChart3 className="h-6 w-6 text-gray-300" />
                        <p className="text-sm font-medium text-gray-900">No performance data yet</p>
                        <p className="text-xs text-gray-500">No spend, clicks, or conversions were recorded for the selected period.</p>
                      </div>
                    </Card>
                  );
                }
                // Compare-period Δ lines (#6276) — the toolbar's comparison mode
                // was previously invisible on this tab.
                const p = previousTotals;
                const metrics = [
                  <MetricCard key="roas" label="ROAS" value={`${totals.roas.toFixed(1)}x`} delta={<DeltaLine current={totals.roas} previous={p?.roas} />} />,
                  <MetricCard key="conv" label="Conversions" value={totals.conversions.toFixed(0)} subtitle={`Rate: ${totals.conversionRate.toFixed(2)}%`} delta={<DeltaLine current={totals.conversions} previous={p?.conversions} />} />,
                  <MetricCard key="spend" label="Spend" value={fmt(totals.cost, cs)} delta={<DeltaLine current={totals.cost} previous={p?.cost} direction="neutral" />} />,
                  <MetricCard key="cpa" label="CPA" value={fmt(totals.cpa, cs)} delta={<DeltaLine current={totals.cpa} previous={p?.cpa} direction="lower-better" />} />,
                  <MetricCard key="clicks" label="Clicks" value={totals.clicks.toLocaleString()} subtitle={`CTR: ${totals.ctr.toFixed(2)}%`} delta={<DeltaLine current={totals.clicks} previous={p?.clicks} />} />,
                  <MetricCard key="imp" label="Impressions" value={totals.impressions.toLocaleString()} delta={<DeltaLine current={totals.impressions} previous={p?.impressions} />} />,
                  <MetricCard key="cr" label="Conv Rate" value={`${totals.conversionRate.toFixed(1)}%`} delta={<DeltaLine current={totals.conversionRate} previous={p?.conversionRate} />} />,
                ];

                if (widgetWidth === 33) {
                  // 2 + 2 + 3
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">{metrics.slice(0, 2)}</div>
                      <div className="grid grid-cols-2 gap-3">{metrics.slice(2, 4)}</div>
                      <div className="grid grid-cols-3 gap-3">{metrics.slice(4)}</div>
                    </div>
                  );
                }
                if (widgetWidth === 50) {
                  // 3 + 4
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">{metrics.slice(0, 3)}</div>
                      <div className="grid grid-cols-4 gap-3">{metrics.slice(3)}</div>
                    </div>
                  );
                }
                if (widgetWidth === 66) {
                  // 4 + 3 (both rows fill full width)
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-3">{metrics.slice(0, 4)}</div>
                      <div className="grid grid-cols-3 gap-3">{metrics.slice(4)}</div>
                    </div>
                  );
                }
                // 100%
                return <div className="grid grid-cols-4 md:grid-cols-7 gap-3">{metrics}</div>;
              }

              case 'money-context': {
                // Each metric card stacks label on top, value + delta on the
                // same row below, caption underneath. Stacked-vertical inside
                // the card avoids the prior label+delta side-by-side layout
                // that overflowed narrow widget widths.
                const renderTrendCard = (
                  label: string,
                  value: string,
                  delta: number,
                  improvingWhen: 'down' | 'up',
                  captionStable: string,
                  captionImproving: string,
                  captionWorsening: string,
                ) => {
                  const improving = improvingWhen === 'down' ? delta < 0 : delta > 0;
                  const worsening = improvingWhen === 'down' ? delta > 0 : delta < 0;
                  return (
                    <div className="p-3 rounded-lg bg-gray-50">
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
                        {delta !== 0 && (
                          <div className={`flex items-center gap-0.5 text-xs font-medium shrink-0 ${improving ? 'text-green-600' : 'text-red-600'}`}>
                            {(improvingWhen === 'down' ? delta < 0 : delta > 0) ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                            {Math.abs(delta).toFixed(1)}%
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1.5">
                        {delta === 0 ? captionStable : improving ? captionImproving : worsening ? captionWorsening : captionStable}
                      </p>
                    </div>
                  );
                };
                const hasTrends = !!(spendTrends?.cpa.hasData || spendTrends?.roas.hasData);
                const hasBudgets = budgetUtilisation.length > 0;
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Spend Efficiency</h4>
                    {(hasTrends || hasBudgets) ? (
                      // Stacked layout: trend cards sit in a 2-col row on top,
                      // budget bars span the full width beneath. Avoids the
                      // narrow-column overflow the prior 4/8 split caused
                      // when the widget renders in a sidebar-width container.
                      <div className="space-y-4">
                        {hasTrends && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {spendTrends?.cpa.hasData && renderTrendCard(
                              'Cost per Conversion',
                              `${cs}${spendTrends.cpa.current.toFixed(2)}`,
                              spendTrends.cpa.delta,
                              'down',
                              'Stable vs previous period',
                              'Improving vs previous period',
                              'Increasing vs previous period',
                            )}
                            {spendTrends?.roas.hasData && renderTrendCard(
                              'Return on Ad Spend',
                              `${spendTrends.roas.current.toFixed(2)}x`,
                              spendTrends.roas.delta,
                              'up',
                              'Stable vs previous period',
                              'Improving vs previous period',
                              'Declining vs previous period',
                            )}
                          </div>
                        )}
                        {hasBudgets && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Campaign Budget Utilisation</p>
                            <div className="space-y-2.5">
                              {budgetUtilisation.slice(0, 5).map((item) => (
                                <div key={item.name}>
                                  <div className="flex items-center justify-between gap-3 text-xs mb-1">
                                    <span className="text-gray-700 font-medium truncate">{item.name}</span>
                                    <span className="text-gray-500 whitespace-nowrap shrink-0">
                                      {cs}{item.dailySpend.toFixed(0)} / {cs}{item.dailyBudget.toFixed(0)} ({item.utilisation.toFixed(0)}%)
                                    </span>
                                  </div>
                                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, item.utilisation)}%`, background: item.color }} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <EmptyChart icon={BarChart3} message="Spend efficiency data will appear once campaigns have performance and budget data" />
                    )}
                  </Card>
                );
              }

              case 'performance-chart':
                return renderChart();

              case 'conversion-mix-donut': {
                // #4301 — Conversion mix by action. Distinct from the
                // 'conversions-donut' widget above which slices by campaign
                // type; this one slices by the conversion action itself
                // ("Calls from ads", "Form submissions (GTM)", …) matching
                // the team's weekly client report.
                const top = conversionMixByAction.slice(0, 6);
                const totalConv = top.reduce((s, a) => s + a.conversions, 0);
                return (
                  <Card className="p-4 border-0 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Conversion Mix</h4>
                    {isConversionActionsLoading && top.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <Spinner size="md" className="text-gray-400" />
                      </div>
                    ) : top.length > 0 ? (
                      <div className="flex items-center">
                        <ResponsiveContainer width="40%" height={160}>
                          <PieChart>
                            <Pie data={top} cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="conversions" strokeWidth={0}>
                              {top.map((_, i) => <Cell key={i} fill={CHART_PURPLE[i % CHART_PURPLE.length]} />)}
                            </Pie>
                            <Tooltip
                              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                              formatter={(value) => [`${Number(value).toFixed(0)} conv.`, '']}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-1.5">
                          {top.map((item, i) => {
                            const pct = totalConv > 0 ? (item.conversions / totalConv) * 100 : 0;
                            return (
                              <div key={item.name} className="flex items-center justify-between text-xs gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: CHART_PURPLE[i % CHART_PURPLE.length] }} />
                                  <span className="text-gray-600 truncate">{item.name}</span>
                                </div>
                                <span className="font-medium text-gray-900 whitespace-nowrap">{item.conversions.toFixed(0)} <span className="text-gray-400 font-normal">({pct.toFixed(0)}%)</span></span>
                              </div>
                            );
                          })}
                          {conversionMixByAction.length > top.length && (
                            <p className="text-[10px] text-gray-400 pt-0.5">+{conversionMixByAction.length - top.length} more</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <EmptyChart icon={PieChartIcon} message="Conversion mix will appear once tracked actions record conversions" />
                    )}
                    {/* #4306 — auto-explainer footnote. */}
                    {(() => {
                      const narrative = explainConversionMix(conversionActionsData ?? []);
                      return narrative ? <p className="text-xs text-gray-400 mt-2">{narrative}</p> : null;
                    })()}
                  </Card>
                );
              }

              case 'type-efficiency': {
                // Combined replacement for the prior spend-donut +
                // conversions-donut. Merges the same data into one info-dense
                // table-style list with spend, conversions, share-of-total
                // mini-bars, and a derived CPA column so users can spot
                // efficiency differences at a glance. Keeps the same CHART_PURPLE
                // colour mapping by-index so types stay visually consistent
                // with everything else (campaigns chart, by-type bars).
                //
                // Joined by name across costByType + convByType — they should
                // share the same set of categories but the join is defensive.
                const convByName = new Map(convByType.map(c => [c.name, c.value]));
                const totalSpend = costByType.reduce((s, r) => s + r.value, 0);
                const totalConv = convByType.reduce((s, r) => s + r.value, 0);
                const rows = costByType.map((row, i) => {
                  const conv = convByName.get(row.name) ?? 0;
                  return {
                    name: row.name,
                    color: CHART_PURPLE[i % CHART_PURPLE.length],
                    spend: row.value,
                    spendShare: totalSpend > 0 ? row.value / totalSpend : 0,
                    conv,
                    convShare: totalConv > 0 ? conv / totalConv : 0,
                    cpa: conv > 0 ? row.value / conv : 0,
                  };
                });
                // Also include conversion-only categories that didn't appear
                // in costByType (rare but possible if a campaign converts but
                // has zero recorded spend in this window).
                const spendNames = new Set(costByType.map(r => r.name));
                convByType.forEach((row, i) => {
                  if (!spendNames.has(row.name)) {
                    rows.push({
                      name: row.name,
                      color: CHART_PURPLE[(costByType.length + i) % CHART_PURPLE.length],
                      spend: 0, spendShare: 0,
                      conv: row.value,
                      convShare: totalConv > 0 ? row.value / totalConv : 0,
                      cpa: 0,
                    });
                  }
                });
                rows.sort((a, b) => b.spend - a.spend);
                const hasData = rows.length > 0;
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-baseline justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Performance by Campaign Type</h4>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400">Spend · Conversions · CPA</p>
                    </div>
                    {hasData ? (
                      <div>
                        {/* Header row */}
                        <div className="grid grid-cols-[1.25fr_2fr_2fr_0.85fr] gap-3 text-[10px] uppercase tracking-wider text-gray-400 mb-2 px-1">
                          <span>Type</span>
                          <span>Spend</span>
                          <span>Conversions</span>
                          <span className="text-right">CPA</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {rows.map(row => (
                            <div key={row.name} className="grid grid-cols-[1.25fr_2fr_2fr_0.85fr] gap-3 items-center py-2 px-1">
                              {/* Type chip */}
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.color }} />
                                <span className="text-xs font-medium text-gray-900 truncate">{row.name}</span>
                              </div>
                              {/* Spend: value + share mini-bar */}
                              <div>
                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-900">{fmt(row.spend, cs)}</span>
                                  <span className="text-[10px] text-gray-500">{(row.spendShare * 100).toFixed(0)}%</span>
                                </div>
                                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${row.spendShare * 100}%`, background: row.color }} />
                                </div>
                              </div>
                              {/* Conversions: value + share mini-bar */}
                              <div>
                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-900">{row.conv.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                                  <span className="text-[10px] text-gray-500">{(row.convShare * 100).toFixed(0)}%</span>
                                </div>
                                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${row.convShare * 100}%`, background: row.color, opacity: 0.55 }} />
                                </div>
                              </div>
                              {/* CPA — the actionable derived metric. Faded
                                  when there are no conversions to divide by. */}
                              <div className="text-right">
                                <span className={`text-xs font-medium ${row.cpa > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                                  {row.cpa > 0 ? fmt(row.cpa, cs) : '—'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <EmptyChart icon={PieChartIcon} message="Campaign-type breakdown will appear once campaigns have spend or conversions" />
                    )}
                  </Card>
                );
              }

              case 'ai-actions':
                // Empty-state path always returns a titled card so the widget
                // slot reads as "Recent Optimizations · waiting for activity"
                // instead of just showing the kebab over empty space.
                if (actionLogs.length === 0) {
                  return (
                    <Card className="p-4 border border-gray-200 shadow-sm bg-white">
                      <div className="flex items-center justify-between mb-3 pr-7">
                        <h4 className="text-sm font-semibold text-gray-900">Recent Optimizations</h4>
                      </div>
                      <EmptyChart icon={Sparkles} message="Optimizations will appear once Glitch has tuned this account" />
                    </Card>
                  );
                }
                return (
                  // `relative` anchors the absolute-positioned "View all →"
                  // link below — it lifts up to the same vertical lane as
                  // the WidgetGrid resize/reorder kebab (which lives at
                  // top:8/right:8 of the WidgetGrid wrapper) so the two
                  // controls share one row at the top-right of the card.
                  <Card className="p-4 border border-gray-200 shadow-sm bg-white relative">
                    <button
                      type="button"
                      onClick={() => onNavigateTab('activity')}
                      className="absolute top-2 right-10 text-xs text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors z-10"
                    >
                      View all →
                    </button>
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">Recent Optimizations</h4>
                        {/* The feed is fetched with limit=100 — a full page is a cap, not a total (#6281). */}
                        <span className="text-[10px] text-gray-400 truncate">
                          · {actionLogs.length >= 100 ? '100+ (most recent shown)' : `${actionLogs.length} this period`}
                        </span>
                      </div>
                    </div>
                    <div className="relative ml-3">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-100" />
                      {actionLogs.slice(0, 5).map((action) => {
                        const campaignName = action.campaign?.name || action.campaign_name;
                        // Pretty action-type — falls back to enum-string with
                        // underscores formatted out (ADD_NEGATIVE_KEYWORD → "Add negative keyword").
                        const typeLabel = ACTION_TYPE_LABELS[action.action_type]
                          || String(action.action_type).toLowerCase().replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
                        return (
                          <div key={action.id} className="relative pl-5 pb-3 last:pb-0">
                            <div className={cn(
                              'absolute left-[-5px] top-1.5 w-3 h-3 rounded-full border-2 border-white z-10',
                              action.is_undone ? 'bg-gray-300' : 'bg-[#290BD9]'
                            )} />
                            {/* Top row: type badge + relative time + undone tag */}
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
                                ACTION_TYPE_COLORS[action.action_type] || 'bg-gray-100 text-gray-800',
                                action.is_undone && 'opacity-60',
                              )}>
                                {typeLabel}
                              </span>
                              <span className="text-[10px] text-gray-400">{formatActivityTimestamp(action.created_at)}</span>
                              {action.is_undone && (
                                <span className="text-[10px] uppercase tracking-wider text-gray-400">Undone</span>
                              )}
                            </div>
                            {/* Campaign name. parseCampaignName strips the
                                "Glitch | Brand | " prefix to keep the line short. */}
                            {campaignName && (
                              <p className={cn(
                                'text-xs font-medium text-gray-900 mb-0.5',
                                action.is_undone && 'line-through text-gray-500',
                              )}>
                                {parseCampaignName(campaignName)}
                              </p>
                            )}
                            {/* Human-readable summary — line-clamped to 2 so
                                a long LLM-written reason doesn't blow the
                                card height but still surfaces context. */}
                            {action.log && (
                              <p className="text-xs text-gray-500 leading-snug line-clamp-2">{action.log}</p>
                            )}
                            {/* Why — when the backend recorded a reason
                                distinct from the log message (e.g. detector
                                explanation), show it as smaller meta text. */}
                            {action.reason && action.reason !== action.log && (
                              <p className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-1">{action.reason}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );

              case 'top-performers':
                if (!hasData) {
                  return (
                    <Card className="p-4 border border-gray-200 shadow-sm bg-white">
                      <div className="flex items-center justify-between mb-3 pr-7">
                        <h4 className="text-sm font-semibold text-gray-900">Top Performers</h4>
                      </div>
                      <EmptyChart icon={Trophy} message="Top campaigns by conversions and top ads by CTR will appear once data flows in" />
                    </Card>
                  );
                }
                return (
                  <Card className="p-4 border border-gray-200 shadow-sm bg-white relative">
                    <button
                      type="button"
                      onClick={() => onNavigateTab('campaigns')}
                      className="absolute top-2 right-10 text-xs text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors z-10"
                    >
                      View all →
                    </button>
                    <div className="flex items-center justify-between mb-3 pr-7">
                      <h4 className="text-sm font-semibold text-gray-900">Top Performers</h4>
                    </div>

                    {typeMetrics.length > 0 && (() => {
                      const bestType = [...typeMetrics].sort((a, b) => b.roas - a.roas)[0];
                      return bestType.roas > 0 ? (
                        <div className="mb-3 p-2.5 rounded-lg bg-green-50 border border-green-100">
                          <p className="text-xs font-medium text-green-800">
                            {bestType.label} campaigns have {bestType.roas.toFixed(1)}x ROAS
                          </p>
                          <p className="text-[10px] text-green-600">
                            {bestType.conversions.toFixed(0)} conversions from {fmt(bestType.cost, cs)} spend
                          </p>
                        </div>
                      ) : null;
                    })()}

                    <div className="grid grid-cols-2 gap-8">
                      {(() => {
                        const top3 = [...filteredCampaigns].sort((a, b) => b.conversions - a.conversions).slice(0, 3);
                        const topConvData = top3.map((c, i) => ({ name: c.name, value: c.conversions, color: CHART_PURPLE[i], roas: c.roas }));
                        const totalConv = topConvData.reduce((s, d) => s + d.value, 0);
                        return (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Top campaigns by conversions</p>
                            <div className="flex justify-center">
                              <ResponsiveContainer width={100} height={100}>
                                <PieChart>
                                  <Pie data={topConvData} cx="50%" cy="50%" innerRadius={28} outerRadius={42} dataKey="value" strokeWidth={0}>
                                    {topConvData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="space-y-1.5 mt-1">
                              {topConvData.map((d, i) => (
                                <div key={i} className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                                    <p className="text-xs text-gray-900 truncate">{d.name}</p>
                                  </div>
                                  <div className="text-right shrink-0 ml-2">
                                    <span className="text-xs font-bold text-gray-900">{d.value.toFixed(0)}</span>
                                    <span className="text-[10px] text-gray-400 ml-1">({totalConv > 0 ? ((d.value / totalConv) * 100).toFixed(0) : 0}%)</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {adMetrics.length > 0 && (() => {
                        const top3 = [...adMetrics].sort((a, b) => b.ctr - a.ctr).slice(0, 3);
                        const topCtrData = top3.map((ad, i) => ({ name: ad.adDisplayName, value: ad.ctr, color: CHART_PURPLE[i], campaign: ad.campaignDisplayName }));
                        return (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Top ads by CTR</p>
                            <div className="flex justify-center">
                              <ResponsiveContainer width={100} height={100}>
                                <PieChart>
                                  <Pie data={topCtrData} cx="50%" cy="50%" innerRadius={28} outerRadius={42} dataKey="value" strokeWidth={0}>
                                    {topCtrData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="space-y-1.5 mt-1">
                              {topCtrData.map((d, i) => (
                                <div key={i} className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                                    <div className="min-w-0">
                                      <p className="text-xs text-gray-900 truncate">{d.name}</p>
                                      <p className="text-[10px] text-gray-400 truncate">{d.campaign}</p>
                                    </div>
                                  </div>
                                  <span className="text-xs font-bold text-gray-900 shrink-0 ml-2">{d.value.toFixed(2)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </Card>
                );

              default:
                return null;
            }
          }}
        </WidgetGrid>
  );
}
